import { useEffect, useState, useCallback } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Modal } from '@/components/ui/modal';
import {
  Plus,
  Archive,
  RotateCw,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';
import { adminApi, RatePlan, CreateRatePlanRequest } from '../services/adminApi';

// Map the Fluent Badge color names still returned by getStatusBadge onto shadcn Badge classes,
// preserving the status color SEMANTICS after the migration: success = emerald, warning = amber,
// danger = red, informative/subtle = neutral gray. Mirrors the helper in OrgBillingTab (U9).
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    return (tokens / 1_000_000).toFixed(1) + 'M';
  }
  return tokens.toLocaleString();
}

function formatPrice(amount: number): string {
  return '$' + amount.toFixed(2);
}

function formatOverageRate(rate: number): string {
  return '$' + rate.toFixed(3) + '/1K';
}

function RatePlansTab() {
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRatePlanRequest>({
    productId: '',
    name: '',
    description: '',
    basePrice: 0,
    includedTokens: 0,
    overageRatePerThousand: 0,
    stripePriceId: '',
    billingInterval: 'monthly',
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Archive confirmation state
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archivingPlan, setArchivingPlan] = useState<RatePlan | null>(null);

  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignOrgId, setAssignOrgId] = useState('');
  const [assignProductId, setAssignProductId] = useState('');
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const fetchRatePlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.listRatePlans(
        undefined,
        statusFilter || undefined
      );
      setRatePlans(data);
      setError(null);
    } catch (err: unknown) {
      let errorMsg = 'Failed to fetch rate plans';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string; details?: string }; status?: number } };
        if (axiosErr.response?.data?.details) {
          errorMsg = `${axiosErr.response.data.error}: ${axiosErr.response.data.details}`;
        } else if (axiosErr.response?.data?.error) {
          errorMsg = axiosErr.response.data.error;
        }
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // statusFilter starts '' so this also covers the initial mount fetch.
  useEffect(() => {
    fetchRatePlans();
  }, [statusFilter]);

  const handleRefresh = () => {
    fetchRatePlans();
  };

  // Summary calculations
  const totalPlans = ratePlans.length;
  const activePlans = ratePlans.filter(p => p.status === 'active').length;
  const distinctProducts = new Set(ratePlans.map(p => p.productId)).size;

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, 'success' | 'warning' | 'informative'> = {
      active: 'success',
      archived: 'informative',
      draft: 'warning',
    };
    return (
      <Badge variant="outline" className={badgeClass(statusColors[status] || 'informative')}>
        {status}
      </Badge>
    );
  };

  // Create plan handler
  const handleCreate = async () => {

    setCreateSubmitting(true);
    setCreateError(null);
    try {
      const payload: CreateRatePlanRequest = {
        productId: createForm.productId,
        name: createForm.name,
        basePrice: createForm.basePrice,
        includedTokens: createForm.includedTokens,
        overageRatePerThousand: createForm.overageRatePerThousand,
      };
      if (createForm.description) payload.description = createForm.description;
      if (createForm.stripePriceId) payload.stripePriceId = createForm.stripePriceId;
      if (createForm.billingInterval) payload.billingInterval = createForm.billingInterval;

      await adminApi.createRatePlan(payload);
      setCreateOpen(false);
      setCreateForm({
        productId: '',
        name: '',
        description: '',
        basePrice: 0,
        includedTokens: 0,
        overageRatePerThousand: 0,
        stripePriceId: '',
        billingInterval: 'monthly',
      });
      fetchRatePlans();
    } catch (err: unknown) {
      let errorMsg = 'Failed to create rate plan';
      if (err instanceof Error) errorMsg = err.message;
      setCreateError(errorMsg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  // Archive handler
  const handleArchive = async () => {
    if (!archivingPlan) return;

    try {
      await adminApi.archiveRatePlan(archivingPlan.id);
      setArchiveOpen(false);
      setArchivingPlan(null);
      fetchRatePlans();
    } catch (err) {
      console.error('Failed to archive rate plan:', err);
    }
  };

  // Assign handler
  const handleAssign = async () => {
    if (!assignOrgId || !assignProductId || !assignPlanId) return;

    setAssignSubmitting(true);
    setAssignError(null);
    try {
      await adminApi.assignRatePlan(assignOrgId, assignProductId, assignPlanId);
      setAssignOpen(false);
      setAssignOrgId('');
      setAssignProductId('');
      setAssignPlanId('');
      fetchRatePlans();
    } catch (err: unknown) {
      let errorMsg = 'Failed to assign rate plan';
      if (err instanceof Error) errorMsg = err.message;
      setAssignError(errorMsg);
    } finally {
      setAssignSubmitting(false);
    }
  };

  const activePlanOptions = ratePlans.filter(p => p.status === 'active');

  if (loading && ratePlans.length === 0) {
    return (
      <div className="admin-loading">
        <Spinner size={32} label="Loading rate plans..." />
      </div>
    );
  }

  return (
    <div className="organizations-tab">
      {error && (
        <div className="admin-error">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-grid">
        <Card className="summary-card">
          <h3>Total Plans</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">All rate plans</p>
          <div className="summary-value">
            <BarChart3 />
            <span>{totalPlans}</span>
          </div>
        </Card>

        <Card className="summary-card">
          <h3>Active Plans</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Currently active</p>
          <div className="summary-value">
            <BarChart3 />
            <span>{activePlans}</span>
          </div>
        </Card>

        <Card className="summary-card">
          <h3>Products</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Distinct products with plans</p>
          <div className="summary-value">
            <BarChart3 />
            <span>{distinctProducts}</span>
          </div>
        </Card>
      </div>

      {/* Rate Plans Table */}
      <div className="subscriptions-section">
        <div className="section-header">
          <h2>Rate Plans</h2>
          <div className="action-buttons">
            <Button
              onClick={() => {
                setCreateError(null);
                setCreateOpen(true);
              }}
            >
              <Plus />
              Create Plan
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setAssignError(null);
                setAssignOrgId('');
                setAssignProductId('');
                setAssignPlanId('');
                setAssignOpen(true);
              }}
            >
              <Plus />
              Assign to Org
            </Button>
            <Button variant="ghost" onClick={handleRefresh}>
              <RotateCw />
              Refresh
            </Button>
          </div>
        </div>

        <div className="filters-row">
          <Select
            value={statusFilter || 'all'}
            onValueChange={(value) => setStatusFilter(value === 'all' ? '' : value)}
          >
            <SelectTrigger aria-label="Filter by status" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Product</th>
              <th>Base Price</th>
              <th>Included Tokens</th>
              <th>Overage Rate</th>
              <th>Status</th>
              <th>Assigned Orgs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ratePlans.map(plan => (
              <tr
                key={plan.id}
                className="clickable-row"
                onClick={() => { window.location.href = `/admin/rate-plans/${plan.id}`; }}
                style={{ cursor: 'pointer' }}
              >
                <td>{plan.name}</td>
                <td><code>{plan.productId}</code></td>
                <td>{formatPrice(plan.basePrice)}</td>
                <td>{formatTokens(plan.includedTokens)}</td>
                <td>{formatOverageRate(plan.overageRatePerThousand)}</td>
                <td>{getStatusBadge(plan.status)}</td>
                <td>{plan.assignedOrganizationCount}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    {plan.status !== 'archived' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Archive"
                        onClick={() => {
                          setArchivingPlan(plan);
                          setArchiveOpen(true);
                        }}
                      >
                        <Archive />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {ratePlans.length === 0 && (
              <tr>
                <td colSpan={8}>No rate plans found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Plan Dialog */}
      <Modal open={createOpen} onOverlayClick={() => setCreateOpen(false)}>
        <Modal.Content maxWidth="2xl" aria-labelledby="create-plan-title">
          <Modal.Header className="px-6 py-4">
            <h2 id="create-plan-title" className="text-lg font-semibold text-foreground">Create Rate Plan</h2>
          </Modal.Header>
          <Modal.Body className="px-6 py-4">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="mb-1 block text-sm font-semibold">Product ID</label>
                <Input
                  value={createForm.productId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, productId: e.target.value }))}
                  placeholder="e.g., parslee-core"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Name</label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Core Standard Monthly"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Description</label>
                <Textarea
                  value={createForm.description || ''}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Plan description..."
                  rows={2}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Base Price (USD)</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={String(createForm.basePrice)}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))}
                    className="pl-6"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Included Tokens</label>
                <Input
                  type="number"
                  value={String(createForm.includedTokens)}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, includedTokens: parseInt(e.target.value) || 0 }))}
                  placeholder="e.g., 5000000"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Overage Rate (per 1K tokens)</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={String(createForm.overageRatePerThousand)}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, overageRatePerThousand: parseFloat(e.target.value) || 0 }))}
                    className="pl-6"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Stripe Price ID (optional)</label>
                <Input
                  value={createForm.stripePriceId || ''}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, stripePriceId: e.target.value }))}
                  placeholder="price_..."
                />
              </div>
              {createError && (
                <div className="text-sm text-destructive">
                  {createError}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!createForm.productId || !createForm.name || createSubmitting}
            >
              {createSubmitting ? 'Creating...' : 'Create Plan'}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      {/* Archive Confirmation Dialog */}
      <Modal open={archiveOpen} onOverlayClick={() => setArchiveOpen(false)}>
        <Modal.Content maxWidth="md" role="alertdialog" aria-labelledby="archive-plan-title">
          <Modal.Header className="px-6 py-4">
            <h2 id="archive-plan-title" className="text-lg font-semibold text-foreground">Archive Rate Plan</h2>
          </Modal.Header>
          <Modal.Body className="px-6 py-4">
            {archivingPlan && (
              <p className="text-sm">
                Are you sure you want to archive <b>{archivingPlan.name}</b>?
                This plan has {archivingPlan.assignedOrganizationCount} assigned organization(s).
                Archived plans cannot be assigned to new organizations.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleArchive}>
              Archive
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      {/* Assign to Org Dialog */}
      <Modal open={assignOpen} onOverlayClick={() => setAssignOpen(false)}>
        <Modal.Content maxWidth="lg" aria-labelledby="assign-plan-title">
          <Modal.Header className="px-6 py-4">
            <h2 id="assign-plan-title" className="text-lg font-semibold text-foreground">Assign Rate Plan to Organization</h2>
          </Modal.Header>
          <Modal.Body className="px-6 py-4">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="mb-1 block text-sm font-semibold">Organization ID</label>
                <Input
                  value={assignOrgId}
                  onChange={(e) => setAssignOrgId(e.target.value)}
                  placeholder="org_..."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Product ID</label>
                <Input
                  value={assignProductId}
                  onChange={(e) => setAssignProductId(e.target.value)}
                  placeholder="e.g., parslee-core"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">Rate Plan</label>
                <Select
                  value={assignPlanId || undefined}
                  onValueChange={(value) => setAssignPlanId(value)}
                >
                  <SelectTrigger aria-label="Filter by status" className="w-full">
                    <SelectValue placeholder="Select a plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activePlanOptions.map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} ({plan.productId}) - {formatPrice(plan.basePrice)}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {assignError && (
                <div className="text-sm text-destructive">
                  {assignError}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!assignOrgId || !assignProductId || !assignPlanId || assignSubmitting}
            >
              {assignSubmitting ? 'Assigning...' : 'Assign Plan'}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default RatePlansTab;
